/**
 * Scout v0 — workspace identity editor (§1.3 layer 1).
 *
 * The layer-1 always-on archetype identity: the base company identity that
 * defaults into EVERY composed search for the workspace. Compose rejects an
 * empty identity (400 IDENTITY_MISSING, P15), so this panel is the surface
 * that unblocks the composer — it lives on the same workspace criteria screen
 * as the always-on suppression lists.
 *
 * Sends identity only on save; the profile PUT preserves the stored
 * suppression lists when the field is omitted.
 */

import { useState } from 'react';
import { useScoutProfile, useUpdateScoutProfile } from '@/lib/scout-queries';
import { getErrorMessage } from '@/lib/queries';
import { useToast } from '@/components/ui/useToast';
import { ErrorBanner, LoadingSpinner } from '@/components/ui';

export function IdentityPanel() {
  const { data: profile, isLoading, error, refetch } = useScoutProfile();
  const update = useUpdateScoutProfile();
  const { addToast } = useToast();

  // null = no in-progress edit; the textarea shows the saved identity.
  const [draft, setDraft] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="card">
        <LoadingSpinner className="py-6" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorBanner
        message={getErrorMessage(error)}
        retryable={error.retryable}
        onRetry={() => refetch()}
      />
    );
  }

  const saved = profile?.identity ?? '';
  const value = draft ?? saved;
  const trimmed = value.trim();
  const dirty = trimmed !== saved;
  const canSave = dirty && trimmed.length > 0 && !update.isPending;

  const handleSave = () => {
    if (!canSave) return;
    update.mutate(
      { identity: trimmed },
      {
        onSuccess: () => {
          setDraft(null);
          addToast('success', 'Workspace identity saved.');
        },
        onError: (err) => addToast('error', getErrorMessage(err)),
      },
    );
  };

  return (
    <section className="card" aria-labelledby="identity-heading">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 id="identity-heading" className="text-lg font-semibold text-text">
            Workspace identity
          </h2>
          <p className="text-xs text-muted mt-0.5">
            The always-on base identity (layer 1). It rides into every composed search.
          </p>
        </div>
        {!saved && <span className="badge badge-warning">not set</span>}
      </div>

      <p className="text-xs text-muted mb-3">
        Describe who this workspace is and who it places for - e.g. the firm's archetype,
        vertical, and placement focus. Composing a search requires this to be set: it is
        merged into every run alongside the always-on suppressions below.
      </p>

      <div className="space-y-2">
        <label htmlFor="identity-input" className="block text-sm font-medium text-text">
          Base identity
        </label>
        <textarea
          id="identity-input"
          className="input min-h-[80px] text-sm"
          placeholder="e.g. Staffing firm placing admin and medical support roles at US companies under 500 employees"
          value={value}
          onChange={(e) => setDraft(e.target.value)}
          disabled={update.isPending}
        />

        <div className="flex items-center gap-3">
          <button type="button" className="btn-primary" onClick={handleSave} disabled={!canSave}>
            {update.isPending ? 'Saving…' : 'Save identity'}
          </button>
          {dirty && trimmed.length === 0 && (
            <span className="text-xs text-warning">Identity cannot be empty.</span>
          )}
          {update.isError && (
            <span className="text-xs text-warning">{getErrorMessage(update.error)}</span>
          )}
        </div>
      </div>
    </section>
  );
}
